import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { ApiClientConstant, ApiConnector, Table } from '@cureskin/api-client';
import {Entity, EntityEnum} from "@cureskin/api-client-rn";

export interface AutoComparisonIds {
  instantCheckups: Array<any>;
  primaryIds: Array<string>;
  secondaryIds: Array<string>;
}

@Injectable({ providedIn: 'root' })
export class InstantCheckupService {
  async getAutoComparisonIds(
    user: Table.User,
    type: keyof typeof EntityEnum.InstantCheckup.Type,
    followUpCount: number,
  ): Promise<AutoComparisonIds> {
    const types = this.getTypesForClass(type as  keyof typeof EntityEnum.InstantCheckup.ClassSubtypes);
    const instantCheckups: Array<Entity['InstantCheckup']> = JSON.parse(JSON.stringify(await ApiConnector.find(Table.InstantCheckup, {
      where: { user, type: { $in: types }, createdAt: { $gte: moment().subtract(120, 'days').toDate() } },
      descending: 'createdAt',
      project: ['type', 'createdAt', 'aiResponse', 'isMainUser'],
    })));

    return this.selectComparisonIds(type, instantCheckups, followUpCount);
  }

  private getTypesForClass(type: keyof typeof EntityEnum.InstantCheckup.ClassSubtypes): Array<string> {
    return [...EntityEnum.InstantCheckup.ClassSubtypes[type]];
  }

  private selectComparisonIds(
    type: string,
    instantCheckups: Array<Entity['InstantCheckup']>,
    followUpCount: number,
  ): AutoComparisonIds {
    const result: AutoComparisonIds = { instantCheckups, primaryIds: [], secondaryIds: [] };
    const minDiffBtwPhotos = this.getMinDiffBtwPhotos(followUpCount);
    switch (type) {
      case EntityEnum.InstantCheckup.Type.FACE: {
        const frontFaceIds = this.selectTwoComparisonOfType(
          this.getTypesForClass(EntityEnum.InstantCheckup.Type.FRONT_FACE),
          instantCheckups, true, minDiffBtwPhotos);

        const leftSideFaceIds = this.selectTwoComparisonOfType(
          [EntityEnum.InstantCheckup.Type.LEFT_SIDE_FACE], instantCheckups, false, minDiffBtwPhotos);

        const rightSideFaceIds = this.selectTwoComparisonOfType(
          [EntityEnum.InstantCheckup.Type.RIGHT_SIDE_FACE], instantCheckups, false, minDiffBtwPhotos);

        const sideFaceIds = this.selectTwoComparisonOfType(
          [EntityEnum.InstantCheckup.Type.SIDE_FACE], instantCheckups, false, minDiffBtwPhotos);

        if (frontFaceIds.length !== 2) {
          return result;
        }

        result.primaryIds = [frontFaceIds[0], frontFaceIds[1]];
        if (leftSideFaceIds.length === 2) {
          result.secondaryIds = [leftSideFaceIds[0], leftSideFaceIds[1]];
        } else if (rightSideFaceIds.length === 2) {
          result.secondaryIds = [rightSideFaceIds[0], rightSideFaceIds[1]];
        } else if (sideFaceIds.length === 2) {
          result.secondaryIds = [sideFaceIds[0], sideFaceIds[1]];
        }
        break;
      }
      case EntityEnum.InstantCheckup.Type.HAIR: {
        const hairTopIds = this.selectTwoComparisonOfType(
          [EntityEnum.InstantCheckup.Type.HAIR_TOP], instantCheckups, false, minDiffBtwPhotos);
        const hairFrontIds = this.selectTwoComparisonOfType(
          [EntityEnum.InstantCheckup.Type.HAIR_FRONT], instantCheckups, false, minDiffBtwPhotos);
        if (hairTopIds.length !== 2 && hairFrontIds.length !== 2) return result;
        result.primaryIds = [hairFrontIds[0], hairFrontIds[1]];
        break;
      }
      default:
    }
    return result;
  }

  private getMinDiffBtwPhotos(followUpCount: number): number {
    return followUpCount === 1 ? 14 : 21;
  }

  private selectTwoComparisonOfType(
    types: Array<Entity['InstantCheckup']['type'][keyof Entity['InstantCheckup']['type']]>,
    instantCheckups: Array<Entity['InstantCheckup']>,
    forOnlyMainUser: boolean,
    minDiffBtwPhotos: number,
  ): Array<string> {
    const filtered = instantCheckups.filter((each: any) => types.includes(each.type) && (forOnlyMainUser ? each.isMainUser : true));
    if (filtered?.length < 2 && forOnlyMainUser) {
      return this.selectTwoComparisonOfType(types, instantCheckups, false, minDiffBtwPhotos);
    }
    if (!filtered?.length) return [];
    const instantCheckupIds: Array<string> = [];
    instantCheckupIds.push(filtered[0].objectId);
    // eslint-disable-next-line no-restricted-syntax
    for (const each of filtered) {
      if (moment(each.createdAt).isSameOrBefore(moment().subtract(minDiffBtwPhotos, 'days'))) {
        instantCheckupIds.push(each.objectId);
        break;
      }
    }
    if (instantCheckupIds.length !== 2 || instantCheckupIds[0] === instantCheckupIds[1]) {
      if (filtered?.length !== 2 && forOnlyMainUser) {
        return this.selectTwoComparisonOfType(types, instantCheckups, false, minDiffBtwPhotos);
      }
      return [];
    }
    return instantCheckupIds;
  }
}
